
import React from 'react';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export interface FilterOption {
  value: string;
  label: string;
}

interface FilterComboboxProps {
  options: FilterOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  selectedValue: string;
  onSelect: (value: string) => void;
}

export const FilterCombobox: React.FC<FilterComboboxProps> = ({
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  selectedValue,
  onSelect,
}) => {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (currentValue: string) => {
    // Toggle value (if same as selected, clear it, otherwise set it)

    onSelect(currentValue === selectedValue ? "all" : currentValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between min-w-40 bg-background"
          type="button"
        >
          {selectedValue && selectedValue !== "all"
            ? options.find((option) => option.value === selectedValue)?.label || placeholder
            : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-40 z-50 bg-popover">
        <Command>
          <CommandList>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                key="all"
                value="all"
                onSelect={() => handleSelect("all")}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    selectedValue === "all" || !selectedValue ? "opacity-100" : "opacity-0"
                  )}
                />
                {placeholder}
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedValue === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
